import { Box, BarChart as Chart, Text } from '@factorialco/deprecated-design-system'
import { sum } from 'lodash'

import DraggableNode from 'modules/core/components/DraggableNode'
import useMouseCoords from 'modules/core/hooks/useMouseCoords'
import i18n from 'modules/core/lib/i18n'

import BlankSlate from 'modules/insights/components/BlankSlate'
import ChartTooltip from 'modules/insights/components/ChartTooltip'
import ChartTooltipPercentage from 'modules/insights/components/ChartTooltipPercentage'
import TooltipLegend from 'modules/insights/components/TooltipLegend'
import useMarginEffect from 'modules/insights/hooks/useMarginEffect'
import magnitude from 'modules/insights/lib/magnitude'

export interface Data {
  value: number
  name: string
}

interface Props {
  values: Data[]
  title: string
  info?: string
}

const ellipsize = (maxLength: number) => (value: string) =>
  value.length > maxLength ? `${value.substring(0, maxLength)}...` : value

export const BarChart = ({ values, title, info }: Props) => {
  const counts = values.map((value) => value.value)
  const total = sum(counts)
  const maxValue = Math.max(...counts)
  const maxY = magnitude(maxValue)
  const left = useMarginEffect(maxY)
  const coordinates = useMouseCoords()

  const formatBottomAxis = () => {
    if (values.length > 12) return null

    if (values.length > 6) {
      return { format: ellipsize(4) }
    }
    if (values.length > 2) {
      return { format: ellipsize(12) }
    }
    if (values.length > 1) {
      return { format: ellipsize(24) }
    }

    return {}
  }

  return (
    <Box
      maxHeight="s360"
      borderRadius={{ all: 'abs016' }}
      border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
      style={{ display: 'block' }}
      paddingTop="s16"
      paddingBottom="s32"
      paddingX="s32"
    >
      {values.length == 0 && <BlankSlate />}
      {values.length > 0 && (
        <Box style={{ display: 'contents' }} padding="s16">
          <Box flexDirection="row" gap="s4" alignItems="center">
            <Text color="grey1000" size="400" weight="semibold">
              {title}
            </Text>
          </Box>
          <Chart
            margin={{ top: 30, bottom: 25, left, right: 0 }}
            tooltip={({ label, value }) => {
              return (
                <DraggableNode coordinates={coordinates}>
                  <ChartTooltip title={label.toString()}>
                    <Box gap="s8">
                      <TooltipLegend
                        color={'viridian1000'}
                        name={i18n.t('insights.widgets.shared.headcount')}
                        value={value}
                      />
                      <ChartTooltipPercentage percentage={(value / total) * 100} />
                    </Box>
                  </ChartTooltip>
                </DraggableNode>
              )
            }}
            colors={['viridian1000']}
            maxValue={maxY}
            keys={['value']}
            indexBy={'name'}
            data={values.map(({ value, name }) => {
              return { value, name }
            })}
            axisBottom={formatBottomAxis()}
          />
        </Box>
      )}
    </Box>
  )
}
